/**
 * Los casos que al dueño no se le ocurrirían (GKTIBO-76).
 *
 * ## El problema
 *
 * > «Nadie le escribe un reclamo furioso a su propio bot. El dueño prueba lo
 * > que espera que funcione, no lo que puede salir mal.»
 *
 * Es el hallazgo de `ecobio` del 2026-08-28: la herramienta de tickets estaba
 * disponible y no se usaba porque el conocimiento no la nombraba. **Nadie lo
 * habría descubierto preguntándole al bot por los horarios.**
 *
 * ## Se arman desde lo que el tenant declaró, no de una lista fija
 *
 * Esto lo lee un taller, un consultorio y una tienda. «¿Atienden por Sura?» es
 * del mundo médico y no significa nada para una veterinaria (L32). Por eso las
 * preguntas salen de sus servicios y de sus herramientas activas.
 *
 * ## Precargan, no disparan
 *
 * La sugerencia escribe el mensaje en el campo y lo deja editable. No lo envía
 * sola y **no trae una respuesta esperada**: la respuesta sale del agente real
 * o no sale. Una sugerencia que dispara y muestra una respuesta preparada es
 * una demo, no una prueba (G3, L27).
 */

export type TipoDeSugerencia = 'precio' | 'limite' | 'reclamo';

export interface Sugerencia {
  tipo: TipoDeSugerencia;
  /** Se precarga en el campo. Editable. */
  mensaje: string;
  /** Por qué vale la pena probarlo. Se le muestra al dueño. */
  porQue: string;
}

export interface DeQueDispone {
  /** Lo que el dueño cargó como servicios. Puede estar vacío. */
  servicios: string[];
  /** Las herramientas que el bot de este tenant tiene habilitadas. */
  herramientas: string[];
}

export function armarSugerencias({ servicios, herramientas }: DeQueDispone): Sugerencia[] {
  const sugerencias: Sugerencia[] = [];

  // 1. El precio. Es lo que más se pregunta y lo que peor se extrae de un PDF.
  //    Si no cargó servicios NO desaparece: que no haya servicios cargados es
  //    justamente algo que conviene que descubra probando.
  const servicio = servicios[0];
  sugerencias.push({
    tipo: 'precio',
    mensaje: servicio ? `¿Cuánto cuesta ${servicio}?` : '¿Cuánto cuesta lo que ofrecen?',
    porQue: 'Es lo que más te van a preguntar, y los números son lo que peor se lee solo.',
  });

  // 2. Algo que el negocio NO hace. Es GKTIBO-44 en su origen: el bot
  //    prometiendo capacidades y plazos que nadie puede cumplir.
  sugerencias.push({
    tipo: 'limite',
    mensaje: '¿Pueden hacerlo hoy mismo y a domicilio?',
    porQue: 'Para ver si dice que no cuando corresponde, en vez de prometer de más.',
  });

  // 3. Un reclamo, sólo si tiene con qué atenderlo. Ofrecerlo sin la
  //    herramienta sería mandarlo a probar algo que no puede funcionar.
  if (herramientas.includes('openTicket')) {
    sugerencias.push({
      tipo: 'reclamo',
      mensaje: 'Estoy muy molesto, lo que me entregaron llegó mal.',
      porQue: 'Para ver si registra el reclamo en vez de sólo pedir disculpas.',
    });
  }

  return sugerencias;
}
